import { ethers } from 'ethers'

import { config } from '@/config'
import { captureError } from '@/core/errors'
import { i18n } from '@/core/i18n/init'

import itemRegisterer from './abis/ItemRegistererV3.json'
import { connectMetamaskClient } from './metamask'
import { ItemRegistererV3 as ItemRegistererType } from './types/ItemRegistererV3'

type createNftItemInput = {
  itemId: string
  artistId: string
  tokenURI: string
}

type Request = {
  value: number
  gas: number
  nonce: string
  to: string
  from: string
  data: string
}

export const createNftItem = async ({
  itemId,
  artistId,
  tokenURI,
}: createNftItemInput) => {
  await connectMetamaskClient()

  const provider = new ethers.providers.Web3Provider(window.ethereum)
  const signer = provider.getSigner()

  const accounts = await provider.send('eth_requestAccounts', [])

  const contract = new ethers.Contract(
    config.nft.contractAddress.ItemRegisterer,
    itemRegisterer.abi,
    signer
  ) as ItemRegistererType

  const minter = new LazyMinter({
    contract,
    signer,
  })

  const voucher = await minter.createVoucher({ itemId, artistId, tokenURI })

  const data = contract.interface.encodeFunctionData(
    'registerItemVoucherByCreator',
    [
      {
        artistId,
        itemId,
        tokenURI,
        signatureByCreator: voucher.signature,
      },
    ]
  )

  const forwarder = createInstance(provider)
  const request = await buildRequest(forwarder, {
    to: config.nft.contractAddress.ItemRegisterer,
    from: accounts[0],
    data,
  })
  const toSign = await buildTypedData(forwarder, request)
  const signature = await signer.provider.send('eth_signTypedData_v4', [
    accounts[0],
    JSON.stringify(toSign),
  ])

  /**
   * for meta transaction (openzeppelin)
   */
  const metaTxRes = await fetch(config.nft.metaTransactionEndpoint as string, {
    method: 'POST',
    body: JSON.stringify({ signature, request }),
    headers: { 'Content-Type': 'application/json' },
  })
  const metaTxResObj = await metaTxRes.json()

  /**
   * ブロックチェーン側でエラーが発生した時、200番でstatus: 'error'で返ってくるためハンドリング
   */
  if (metaTxResObj.status === 'error') {
    const e = new Error(i18n.t('metaTransactionError', { ns: 'wallet' }))
    captureError(e, 'metaTransaction')
    throw e
  }

  return
}

const EIP712Domain = [
  { name: 'name', type: 'string' },
  { name: 'version', type: 'string' },
  { name: 'chainId', type: 'uint256' },
  { name: 'verifyingContract', type: 'address' },
]

const ForwardRequest = [
  { name: 'from', type: 'address' },
  { name: 'to', type: 'address' },
  { name: 'value', type: 'uint256' },
  { name: 'gas', type: 'uint256' },
  { name: 'nonce', type: 'uint256' },
  { name: 'data', type: 'bytes' },
]

function getMetaTxTypeData(chainId: number, verifyingContract: string) {
  return {
    types: {
      EIP712Domain,
      ForwardRequest,
    },
    domain: {
      name: 'MinimalForwarder',
      version: '0.0.1',
      chainId,
      verifyingContract,
    },
    primaryType: 'ForwardRequest',
  }
}

async function buildTypedData(forwarder: ethers.Contract, request: Request) {
  const chainId = await forwarder.provider.getNetwork().then(n => n.chainId)
  const typeData = getMetaTxTypeData(chainId, forwarder.address)
  return { ...typeData, message: request }
}

async function buildRequest(
  forwarder: ethers.Contract,
  input: {
    to: string
    from: string
    data: string
  }
): Promise<Request> {
  const nonce = await forwarder
    .getNonce(input.from)
    .then((nonce: number) => nonce.toString())
  return { value: 0, gas: 1e6, nonce, ...input }
}

const forwarderAbi = [
  { inputs: [], stateMutability: 'nonpayable', type: 'constructor' },
  {
    inputs: [
      {
        components: [
          { internalType: 'address', name: 'from', type: 'address' },
          { internalType: 'address', name: 'to', type: 'address' },
          { internalType: 'uint256', name: 'value', type: 'uint256' },
          { internalType: 'uint256', name: 'gas', type: 'uint256' },
          { internalType: 'uint256', name: 'nonce', type: 'uint256' },
          { internalType: 'bytes', name: 'data', type: 'bytes' },
        ],
        internalType: 'struct MinimalForwarder.ForwardRequest',
        name: 'req',
        type: 'tuple',
      },
      { internalType: 'bytes', name: 'signature', type: 'bytes' },
    ],
    name: 'execute',
    outputs: [
      { internalType: 'bool', name: '', type: 'bool' },
      { internalType: 'bytes', name: '', type: 'bytes' },
    ],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'from', type: 'address' }],
    name: 'getNonce',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        components: [
          { internalType: 'address', name: 'from', type: 'address' },
          { internalType: 'address', name: 'to', type: 'address' },
          { internalType: 'uint256', name: 'value', type: 'uint256' },
          { internalType: 'uint256', name: 'gas', type: 'uint256' },
          { internalType: 'uint256', name: 'nonce', type: 'uint256' },
          { internalType: 'bytes', name: 'data', type: 'bytes' },
        ],
        internalType: 'struct MinimalForwarder.ForwardRequest',
        name: 'req',
        type: 'tuple',
      },
      { internalType: 'bytes', name: 'signature', type: 'bytes' },
    ],
    name: 'verify',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
]

function createInstance(provider: ethers.providers.Web3Provider) {
  return new ethers.Contract(
    config.nft.contractAddress.forwarderAddress as string,
    forwarderAbi,
    provider
  )
}

class LazyMinter {
  SIGNING_DOMAIN_NAME = 'Utoniq Digital Item'
  SIGNING_DOMAIN_VERSION = '1'

  private _domain: {
    name: string
    version: string
    verifyingContract: string
    chainId: number
  } | null = null
  public signer
  public contract

  constructor({
    contract,
    signer,
  }: {
    contract: ethers.Contract
    signer: ethers.providers.JsonRpcSigner
  }) {
    this.contract = contract
    this.signer = signer
  }

  /**
   * Creates a new NFTVoucher object and signs it using this LazyMinter's signing key.
   *
   * @param {ethers.BigNumber | number} tokenId the id of the un-minted NFT
   * @param {string} uri the metadata URI to associate with this NFT
   * @param {ethers.BigNumber | number} minPrice the minimum price (in wei) that the creator will accept to redeem this NFT. defaults to zero
   *
   * @returns {NFTVoucher}
   */
  async createVoucher(
    voucher: { artistId: string; itemId: string; tokenURI: string },
    minPrice = 0
  ) {
    const domain = await this._signingDomain()

    const signature = await this.signer._signTypedData(
      domain,
      {
        ItemVoucher: [
          { name: 'itemId', type: 'string' },
          { name: 'artistId', type: 'string' },
          { name: 'tokenURI', type: 'string' },
        ],
      },
      voucher
    )

    return {
      ...voucher,
      signature,
    }
  }

  /**
   * @private
   * @returns {object} the EIP-721 signing domain, tied to the chainId of the signer
   */
  async _signingDomain() {
    if (this._domain != null) {
      return this._domain
    }
    const chainId = (await this.contract.getChainID()).toBigInt()
    this._domain = {
      name: this.SIGNING_DOMAIN_NAME,
      version: this.SIGNING_DOMAIN_VERSION,
      verifyingContract: this.contract.address,
      chainId,
    }
    return this._domain
  }
}
